import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GymTvLogo from '../../../images/MyGymTV-white.png';
import { useSelector } from 'react-redux';
import UKEntitySelector from '../Navigation/UKEntitySelector/UKEntitySelector';
import axios from 'axios';
import {
    faMusic,
    faHeadset,
    faImages,
    faMessage,
    faHouse,
    faUser,
    faChartLine,
    faChevronDown,
    faChevronUp,
    faImagePortrait,
    faImage
} from '@fortawesome/free-solid-svg-icons';
import { getUserRoleCategories } from '../../../helpers/roleAccessControl';
import { Link } from 'react-router-dom';
import packageJson from '../../../../package.json';
import './desktop-nav.scss';

const { version } = packageJson;

const DesktopNav = () => {
    const { user, selectedVenue, initialLoginData } = useSelector((state) => state.user);
    const { selectedClubs } = useSelector((state) => state.ukEntities);
    const [dsMenuOpen, setDsMenuOpen] = useState(false);
    const [d6MenuOpen, setD6MenuOpen] = useState(false);

    // Content type availability state
    const [hasD6, setHasD6] = useState(false);
    const [hasDS, setHasDS] = useState(false);

    const roles = user?.RoleIds;
    const userRoles = getUserRoleCategories(user?.RoleIds || []);
    const isUKUser = user?.CountryID === 2;
    const hasUKSelection = selectedClubs.length > 0;

    const showReportsIcon = initialLoginData?.Agreements?.some((agreement) => {
        return agreement.Contracts.some((contract) => contract.MobileUrl !== null);
    });

    // Fetch content type availability when selected clubs change
    useEffect(() => {
        const fetchContentTypes = async () => {
            if (!isUKUser || selectedClubs.length === 0) {
                setHasD6(false);
                setHasDS(false);
                return;
            }

            try {
                const token = localStorage.getItem('token');
                const clubIds = selectedClubs.map(c => c.ClubId);

                const response = await axios.post(
                    `${process.env.REACT_APP_BASE_URL}/api/UK/GetVenueContentTypes`,
                    { clubIds },
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                setHasD6(response.data.HasD6);
                setHasDS(response.data.HasDS);
            } catch (error) {
                console.error('Error fetching content types:', error);
                // On error, default to not showing options
                setHasD6(false);
                setHasDS(false);
            }
        };

        fetchContentTypes();
    }, [selectedClubs, isUKUser]);

    return (
        <div className="desktop side-container px-5">
            <ul
                className="nav flex-sm-column flex-row flex-nowrap flex-shrink-1 flex-sm-grow-0 flex-grow-1 mb-sm-auto mb-0 justify-content-center align-items-center align-items-sm-start"
                id="menu"
            >
                <div className="text-center mx-auto">
                    <img
                        alt="MyGymTV Logo"
                        className="py-4"
                        src={GymTvLogo}
                        style={{ width: '100px' }}
                    />
                </div>

                {/* UK Entity Selector */}
                {isUKUser && <UKEntitySelector />}

                {/* UK Navigation - Only show if venues selected */}
                {isUKUser && hasUKSelection && (
                    <>
                        <li className="nav-item">
                            <Link
                                className="px-sm-0 px-2"
                                to="/"
                            >
                                <FontAwesomeIcon
                                    className="content-management"
                                    icon={faHouse}
                                />
                                <span className="ms-2 d-none d-sm-inline">Dashboard</span>
                            </Link>
                        </li>

                        {/* D6 Dropdown - Only show if venues have D6 players */}
                        {hasD6 && (
                            <li className="nav-item">
                                <div
                                    className="px-sm-0 px-2 dropdown-toggle-custom"
                                    onClick={() => setD6MenuOpen(!d6MenuOpen)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <FontAwesomeIcon
                                        className="content-management"
                                        icon={faImagePortrait}
                                    />
                                    <span className="ms-2 d-none d-sm-inline">D6</span>
                                    <FontAwesomeIcon
                                        className="ms-2"
                                        icon={d6MenuOpen ? faChevronUp : faChevronDown}
                                        size="sm"
                                    />
                                </div>
                                {d6MenuOpen && (
                                    <ul className="submenu ms-4">
                                        <li>
                                            <Link
                                                className="px-sm-0 px-2"
                                                to="/uk/add-d6"
                                            >
                                                Add D6
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                className="px-sm-0 px-2"
                                                to="/uk/review-d6"
                                            >
                                                Review D6
                                            </Link>
                                        </li>
                                    </ul>
                                )}
                            </li>
                        )}

                        {/* DS Dropdown - Only show if venues have DS players */}
                        {hasDS && (
                            <li className="nav-item">
                                <div
                                    className="px-sm-0 px-2 dropdown-toggle-custom"
                                    onClick={() => setDsMenuOpen(!dsMenuOpen)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <FontAwesomeIcon
                                        className="content-management"
                                        icon={faImage}
                                    />
                                    <span className="ms-2 d-none d-sm-inline">DS</span>
                                    <FontAwesomeIcon
                                        className="ms-2"
                                        icon={dsMenuOpen ? faChevronUp : faChevronDown}
                                        size="sm"
                                    />
                                </div>
                                {dsMenuOpen && (
                                    <ul className="submenu ms-4">
                                        <li>
                                            <Link
                                                className="px-sm-0 px-2"
                                                to="/uk/add-ds"
                                            >
                                                Add DS
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                className="px-sm-0 px-2"
                                                to="/uk/review-ds"
                                            >
                                                Review DS
                                            </Link>
                                        </li>
                                    </ul>
                                )}
                            </li>
                        )}
                    </>
                )}

                {/* US User Navigation */}
                {!isUKUser && (userRoles.includes('gymUser') && userRoles.includes('admin')) &&
                    <li className="nav-item">
                        <Link
                            className="px-sm-0 px-2"
                            to="/"
                        >
                            <FontAwesomeIcon
                                className="content-management"
                                icon={faHouse}
                            />
                            <span className="ms-2 d-none d-sm-inline">Dashboard</span>
                        </Link>
                    </li>
                }
                {!isUKUser && selectedVenue !== null && (
                    <>
                        {userRoles.includes('gymUser') && roles.includes(6) &&
                            <li className="nav-item">
                                <Link
                                    className="px-sm-0 px-2"
                                    to="/entertainment"
                                >
                                    <FontAwesomeIcon
                                        className="content-management"
                                        icon={faMusic}
                                    />
                                    <span className="ms-2 d-none d-sm-inline">Entertainment</span>
                                </Link>
                            </li>
                        }
                        {userRoles.includes('gymUser') && roles.includes(1) &&
                            <li className="nav-item">
                                <Link
                                    className="px-sm-0 px-2"
                                    to="/digital-signage"
                                >
                                    <FontAwesomeIcon
                                        className="content-management"
                                        icon={faImages}
                                    />
                                    <span className="ms-2 d-none d-sm-inline">Digital Signage</span>
                                </Link>
                            </li>
                        }
                        {userRoles.includes('gymUser') && roles.includes(7) &&
                            <li className="nav-item">
                                <Link
                                    className="px-sm-0 px-2"
                                    to="/scrolling-text"
                                >
                                    <div className="icon-wrapper d-flex align-content-center">
                                        <FontAwesomeIcon
                                            className="fa-icon account-settnigs"
                                            icon={faMessage}
                                        />
                                        <span className="icon-text">T</span>
                                    </div>
                                    <span className="ms-2">Scrolling Text</span>
                                </Link>
                            </li>
                        }
                    </>
                )}

                {/* Account and Chat sections */}
                {(userRoles.includes('adUser') || (userRoles.includes('admin') && userRoles.includes('adUser'))) &&
                    <>
                        <li className="nav-item">
                            <Link
                                className="px-sm-0 px-2"
                                to="/ad-account"
                            >
                                <FontAwesomeIcon
                                    className="content-management"
                                    icon={faUser}
                                />
                                <span className="ms-2 d-none d-sm-inline">Account</span>
                            </Link>
                        </li>
                        {showReportsIcon && (
                            <li className="nav-item">
                                <Link
                                    className="px-sm-0 px-2"
                                    to="/reports"
                                >
                                    <FontAwesomeIcon
                                        className="content-management"
                                        icon={faChartLine}
                                    />
                                    <span className="ms-2 d-none d-sm-inline">Reports</span>
                                </Link>
                            </li>
                        )}
                    </>
                }
                {userRoles.includes('gymUser') &&
                    <li className="nav-item">
                        <Link
                            className="px-sm-0 px-2"
                            to="/gym-account"
                        >
                            <FontAwesomeIcon
                                className="content-management"
                                icon={faUser}
                            />
                            <span className="ms-2 d-none d-sm-inline">Account</span>
                        </Link>
                    </li>
                }
                {(userRoles.includes('adUser') && !userRoles.includes('admin')) &&
                    <li className="nav-item">
                        <Link
                            className="px-sm-0 px-2"
                            to="https://chat.socialintents.com/c/mygymtv"
                        >
                            <FontAwesomeIcon
                                className="content-management"
                                icon={faHeadset}
                            />
                            <span className="ms-2 d-none d-sm-inline">Chat</span>
                        </Link>
                    </li>
                }
                {(userRoles.includes('gymUser') && !userRoles.includes('admin') || (userRoles.includes('adUser') && userRoles.includes('admin'))) &&
                    <li className="nav-item">
                        <Link
                            className="px-sm-0 px-2"
                            to="https://chat.socialintents.com/c/clubcom"
                        >
                            <FontAwesomeIcon
                                className="content-management"
                                icon={faHeadset}
                            />
                            <span className="ms-2 d-none d-sm-inline">Chat</span>
                        </Link>
                    </li>
                }
            </ul>

            {/* Version at bottom of sidebar */}
            <div className="version-info small mt-auto pb-3 text-center">
                v{version}
            </div>
        </div>
    );
};

export default DesktopNav;